import React, { useEffect, useState, useCallback } from 'react';
import { ThumbsUp, Heart, Lightbulb, Repeat2 } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';

/**
 * Stable per-browser anonymous token. Used for guest reactions so toggling
 * still works and we don't double-count someone who clicks twice.
 * Authenticated users override this server-side via JWT.
 */
const ANON_TOKEN_KEY = 'cca_anon_reaction_token';
const getAnonToken = () => {
  try {
    let t = localStorage.getItem(ANON_TOKEN_KEY);
    if (!t) {
      t = (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`)
        .replace(/-/g, '')
        .slice(0, 32);
      localStorage.setItem(ANON_TOKEN_KEY, t);
    }
    return t;
  } catch {
    return `fallback-${Date.now()}`;
  }
};

const REACTIONS = [
  { type: 'helpful',    Icon: ThumbsUp,  labelEn: 'Helpful',    labelEs: 'Útil',         color: 'text-emerald-500' },
  { type: 'loved',      Icon: Heart,     labelEn: 'Loved',      labelEs: 'Me encantó',   color: 'text-rose-500' },
  { type: 'insightful', Icon: Lightbulb, labelEn: 'Insightful', labelEs: 'Revelador',    color: 'text-amber-500' },
  { type: 'share',      Icon: Repeat2,   labelEn: 'Worth sharing', labelEs: 'Compartible', color: 'text-sky-500' },
];

export const BlogReactions = ({ postId, isSpanish = false }) => {
  const [counts, setCounts] = useState({ helpful: 0, loved: 0, insightful: 0, share: 0 });
  const [userReaction, setUserReaction] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchReactions = useCallback(async () => {
    if (!postId) return;
    const token = getAnonToken();
    const { data } = await apiFetch(`/api/blog/posts/${encodeURIComponent(postId)}/reactions?anon_token=${token}`);
    if (data) {
      setCounts(data.counts || { helpful: 0, loved: 0, insightful: 0, share: 0 });
      setUserReaction(data.user_reaction || null);
    }
  }, [postId]);

  useEffect(() => { fetchReactions(); }, [fetchReactions]);

  const handleReact = async (type) => {
    if (!postId || busy) return;
    setBusy(true);
    // Optimistic update
    const prevReaction = userReaction;
    const prevCounts = counts;
    const next = { ...counts };
    if (prevReaction === type) {
      next[type] = Math.max(0, next[type] - 1);
      setUserReaction(null);
    } else {
      if (prevReaction) next[prevReaction] = Math.max(0, next[prevReaction] - 1);
      next[type] = (next[type] || 0) + 1;
      setUserReaction(type);
    }
    setCounts(next);

    const { data, error } = await apiFetch(`/api/blog/posts/${encodeURIComponent(postId)}/reactions`, {
      method: 'POST',
      body: { type, anon_token: getAnonToken() },
    });
    if (error || !data) {
      // Revert on failure
      setCounts(prevCounts);
      setUserReaction(prevReaction);
    } else {
      setCounts(data.counts);
      setUserReaction(data.user_reaction);
    }
    setBusy(false);
  };

  const total = (counts.helpful || 0) + (counts.loved || 0) + (counts.insightful || 0) + (counts.share || 0);

  return (
    <section
      className="mt-10 pt-6 border-t"
      data-testid="blog-reactions-section"
      aria-label={isSpanish ? 'Reacciones del lector' : 'Reader reactions'}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold">
          {isSpanish ? '¿Te resultó útil?' : 'Was this helpful?'}
        </h3>
        {total > 0 && (
          <span className="text-xs text-muted-foreground" data-testid="blog-reactions-total">
            {total} {isSpanish ? (total === 1 ? 'reacción' : 'reacciones') : (total === 1 ? 'reaction' : 'reactions')}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {REACTIONS.map(({ type, Icon, labelEn, labelEs, color }) => {
          const active = userReaction === type;
          const count = counts[type] || 0;
          return (
            <button
              key={type}
              onClick={() => handleReact(type)}
              disabled={busy}
              data-testid={`blog-reaction-${type}`}
              aria-pressed={active}
              aria-label={isSpanish ? labelEs : labelEn}
              className={[
                'group inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-all',
                'hover:border-[#00E5CC] hover:shadow-sm disabled:opacity-60 disabled:cursor-not-allowed',
                active
                  ? 'border-[#00E5CC] bg-[#00E5CC]/10 font-semibold'
                  : 'border-border bg-card',
              ].join(' ')}
            >
              <Icon className={`h-4 w-4 ${active ? color : 'text-muted-foreground group-hover:' + color}`} />
              <span className="text-xs sm:text-sm">{isSpanish ? labelEs : labelEn}</span>
              {count > 0 && (
                <span
                  className={`ml-1 text-xs tabular-nums ${active ? 'text-foreground' : 'text-muted-foreground'}`}
                  data-testid={`blog-reaction-count-${type}`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default BlogReactions;
